import { nanoid } from 'nanoid'

export const PANEL_OPEN_KEY = 'canvas-left-panel-open'
export const OPEN_TABS_PREFIX = 'canvas-open-chat-tabs-v2'
export const ACTIVE_CHAT_PREFIX = 'canvas-active-chat-v1'
/**
 * Split-mode chat panel width (% of the row). Persisted so the resized
 * ratio survives reloads and panel remounts (full ⇄ split, excluded ⇄
 * split) with no layout shift. Unscoped — one width across every page.
 */
export const CHAT_SIZE_PCT_KEY = 'global-chat-split-size-pct-v1'

export type CanvasChatTab = {
  chatId: string
  isNew: boolean
}

/**
 * Logical scope for chat-state persistence. Each canvas surface (e.g.
 * `/emails`, `/automations`) gets its own scope so chat tabs do not
 * bleed across surfaces.
 *
 * Storage-key behavior:
 *  - `scope == null` OR `scope === 'emails'`: legacy key shape kept
 *    verbatim (`<prefix>:<orgId>:<brandSuffix>`). Existing `/emails`
 *    users keep their stored chat tabs after this hook is extended.
 *  - any other scope: scope is folded into the key
 *    (`<prefix>:<scope>:<orgId>:<brandSuffix>`).
 */
export type CanvasChatScope = 'emails' | 'automations' | string

function withScope(
  prefix: string,
  scope: CanvasChatScope | undefined,
  orgId: string,
  brandId: string | undefined
): string {
  const brandSuffix = brandId ?? 'none'
  if (scope == null || scope === 'emails') {
    return `${prefix}:${orgId}:${brandSuffix}`
  }
  return `${prefix}:${scope}:${orgId}:${brandSuffix}`
}

/**
 * An empty-string brandId is the "brand not resolved yet" sentinel that
 * `resolveActiveBrandId` returns during the load window — NOT a real bucket.
 * Reads/writes are skipped while it's empty so that transient window can never
 * create an orphan `:{orgId}:` bucket (distinct from a real brand and from
 * `none`) that later bleeds into the strip. `undefined` is left as-is (it maps
 * to the intentional `none` bucket for genuinely brand-less surfaces).
 */
function isUnresolvedBrand(brandId: string | undefined): boolean {
  return brandId === ''
}

export function getOpenTabsKey(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): string {
  return withScope(OPEN_TABS_PREFIX, scope, orgId, brandId)
}

export function getActiveChatKey(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): string {
  return withScope(ACTIVE_CHAT_PREFIX, scope, orgId, brandId)
}

export function readStoredActiveChat(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  if (isUnresolvedBrand(brandId)) {
    return null
  }
  try {
    return localStorage.getItem(getActiveChatKey(orgId, brandId, scope))
  } catch {
    return null
  }
}

export function writeStoredActiveChat(
  orgId: string,
  brandId: string | undefined,
  chatId: string | null,
  scope?: CanvasChatScope
): void {
  if (typeof window === 'undefined') {
    return
  }
  if (isUnresolvedBrand(brandId)) {
    return
  }
  try {
    const key = getActiveChatKey(orgId, brandId, scope)
    if (chatId) {
      localStorage.setItem(key, chatId)
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
}

export function readStoredString(key: string, fallback: string): string {
  if (typeof window === 'undefined') {
    return fallback
  }
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

export function writeStoredString(key: string, value: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

export function readStoredTabs(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): Array<CanvasChatTab> {
  if (typeof window === 'undefined') {
    return []
  }
  if (isUnresolvedBrand(brandId)) {
    return []
  }
  try {
    const raw = localStorage.getItem(getOpenTabsKey(orgId, brandId, scope))
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw) as Array<{ chatId: string; isNew: boolean }>
    return parsed
      .filter(
        (t) => typeof t.chatId === 'string' && t.chatId.length > 0 && !t.isNew
      )
      .map((t) => ({ chatId: t.chatId, isNew: false }))
  } catch {
    return []
  }
}

export function writeStoredTabs(
  orgId: string,
  brandId: string | undefined,
  tabs: ReadonlyArray<CanvasChatTab>,
  scope?: CanvasChatScope
): void {
  if (typeof window === 'undefined') {
    return
  }
  if (isUnresolvedBrand(brandId)) {
    return
  }
  try {
    // Persist ONLY real tabs. Ephemeral `isNew` placeholders are recreated by
    // `resolveActiveChat` on the next mount, and `readStoredTabs` already
    // discards them — persisting them is wasted writes + confusing storage
    // (a stored "New chat" that never reloads).
    const persistable = tabs.filter((t) => !t.isNew)
    localStorage.setItem(
      getOpenTabsKey(orgId, brandId, scope),
      JSON.stringify(persistable)
    )
  } catch {
    // ignore
  }
}

export type ResolveActiveChatInput = {
  urlChatId: string | null
  storedTabs: ReadonlyArray<CanvasChatTab>
  storedLastActive: string | null
  /** Optional override for new-tab id generation (used in tests). */
  generateChatId?: () => string
}

export type ResolveActiveChatResult = {
  tabs: Array<CanvasChatTab>
  activeId: string
  /**
   * True when the URL `chat` param did not match any tab on entry, so the
   * caller should also persist the new tabs list.
   */
  appendedTab: boolean
  /**
   * Whether the caller should mirror `activeId` into the `?chat=` URL param.
   * Now always `false` from this resolver: the active chat is seeded in memory
   * (from the URL / localStorage / a fresh id), but the URL is only written on
   * an EXPLICIT open (`setActiveChatId` / `markTabActive`) — never on passive
   * load — so visiting `/home` or a workspace page never auto-appends
   * `?chat=<id>`. Retained as a field so the caller's mirror branch stays
   * explicit (and so a future surface could opt back in).
   */
  shouldMirrorToUrl: boolean
}

/**
 * Pure resolver for `useCanvasChats`. Decides which chat should be active and
 * what the open-tabs list should look like, given the current URL `chat`
 * param, the per-scope localStorage tabs, and the per-scope localStorage
 * last-active.
 *
 * Precedence:
 *   1. URL `chat` param wins. Tab is appended if missing.
 *   2. Otherwise, last-active from localStorage if it still exists in tabs.
 *   3. Otherwise, the last tab in the stored list.
 *   4. Otherwise, a fresh `isNew: true` tab.
 */
export function resolveActiveChat({
  urlChatId,
  storedTabs,
  storedLastActive,
  generateChatId = nanoid,
}: ResolveActiveChatInput): ResolveActiveChatResult {
  if (urlChatId) {
    const hasTab = storedTabs.some((t) => t.chatId === urlChatId)
    if (hasTab) {
      return {
        tabs: [...storedTabs],
        activeId: urlChatId,
        appendedTab: false,
        shouldMirrorToUrl: false,
      }
    }
    return {
      tabs: [...storedTabs, { chatId: urlChatId, isNew: false }],
      activeId: urlChatId,
      appendedTab: true,
      shouldMirrorToUrl: false,
    }
  }

  if (
    storedLastActive &&
    storedTabs.some((t) => t.chatId === storedLastActive)
  ) {
    return {
      tabs: [...storedTabs],
      activeId: storedLastActive,
      appendedTab: false,
      // Seed `activeChatId` in memory only — do NOT auto-write `?chat=` on first
      // paint. Mirroring is now opt-in (an explicit open via setActiveChatId /
      // markTabActive), so loading `/home` or a workspace page never silently
      // rewrites the URL to `?chat=<lastChatId>`. A refresh re-seeds from
      // localStorage regardless, so the panel still restores the last chat.
      shouldMirrorToUrl: false,
    }
  }

  if (storedTabs.length > 0) {
    return {
      tabs: [...storedTabs],
      activeId: storedTabs[storedTabs.length - 1]!.chatId,
      appendedTab: false,
      // Same as the last-active branch: seed in memory, never auto-mirror.
      shouldMirrorToUrl: false,
    }
  }

  const chatId = generateChatId()
  return {
    tabs: [{ chatId, isNew: true }],
    activeId: chatId,
    appendedTab: false,
    // Ephemeral: don't mirror this nanoid into the URL. Without this, a
    // refresh would re-resolve the URL chat id against empty stored tabs
    // and fall into the deep-link branch (isNew: false), which renders
    // an "Untitled" tab + suppresses the "Start to build a new email"
    // empty state on brand-new accounts.
    shouldMirrorToUrl: false,
  }
}

/**
 * Hard cap on simultaneously-open chat tabs. Beyond this, the OLDEST
 * non-active tabs are evicted so the strip can't grow unbounded as the user
 * opens deep links / history chats across a session.
 */
export const MAX_OPEN_TABS = 8

/**
 * Bound the open-tab list to `max`. Evicts the OLDEST tabs (front-first,
 * tabs are appended in open-order) whose `chatId !== activeChatId` until the
 * list is within bounds. The active tab is never evicted. Pure.
 */
export function capTabs(
  tabs: ReadonlyArray<CanvasChatTab>,
  activeChatId: string | null,
  max: number = MAX_OPEN_TABS
): Array<CanvasChatTab> {
  const result = [...tabs]
  let i = 0
  while (result.length > max && i < result.length) {
    if (result[i]!.chatId === activeChatId) {
      i++
      continue
    }
    result.splice(i, 1)
  }
  return result
}

export type AdoptTabInput = {
  tabs: ReadonlyArray<CanvasChatTab>
  /** The currently-active chat id (pre-adopt), or null. */
  activeChatId: string | null
  /** The real chat to make active + ensure present as `isNew: false`. */
  chatId: string
}

export type AdoptTabResult = {
  tabs: Array<CanvasChatTab>
  /** True when the result differs from the input (caller should persist). */
  changed: boolean
}

/**
 * Pure tab-list transition for "make `chatId` the active REAL chat":
 *   (a) ensure `chatId` is present as `{ isNew: false }` — appending if
 *       missing, or promoting an existing `{ isNew: true }` tab of the same id;
 *   (b) when `chatId` is NOT already an open tab (a genuine navigation to a
 *       brand-new chat), drop the previously-active EMPTY placeholder — the
 *       tab whose `chatId === activeChatId` AND `isNew`. This is the
 *       orphan-killer: seeding a fresh "New chat" composer and then landing on
 *       a real chat (brand extraction / launcher / deep link) must not strand
 *       the empty "New chat" tab beside the real one;
 *   (c) cap the result to {@link MAX_OPEN_TABS}.
 *
 * The placeholder is dropped ONLY when adopting a not-yet-open chat. When the
 * target is already a tab we're merely switching to it, so the active `isNew`
 * tab is preserved — it may be one the user just opened with the "+" button
 * (its id IS the active id, but it's their new chat, not an orphan). Dropping
 * it there would delete a tab out from under the user and strand
 * `activeChatId`: the `?chat=` / full-page adopt effects re-fire on
 * `activeChatId` change with a STALE (already-open) target, and this guard is
 * what keeps them safe. A non-active `isNew` tab is likewise never dropped.
 *
 * Returns `changed: false` with an equivalent list when nothing moves, so it
 * is safe to call from a render effect (the callers skip persisting then).
 */
export function adoptTabInto({
  tabs,
  activeChatId,
  chatId,
}: AdoptTabInput): AdoptTabResult {
  // Is the chat we're adopting already an open tab? If so we're just switching
  // to it — never treat the active blank tab as an orphan to drop.
  const hasTarget = tabs.some((tab) => tab.chatId === chatId)

  const isPlaceholder = (tab: CanvasChatTab): boolean =>
    !hasTarget &&
    tab.chatId === activeChatId &&
    tab.isNew &&
    tab.chatId !== chatId

  const next: Array<CanvasChatTab> = []
  for (const tab of tabs) {
    if (isPlaceholder(tab)) {
      continue
    }
    next.push(tab.chatId === chatId ? { chatId, isNew: false } : tab)
  }
  if (!hasTarget) {
    next.push({ chatId, isNew: false })
  }

  // Adopted chat is now active — it must never be the one evicted.
  const capped = capTabs(next, chatId)

  const hasChanged =
    capped.length !== tabs.length ||
    capped.some(
      (tab, i) => tabs[i]?.chatId !== tab.chatId || tabs[i]?.isNew !== tab.isNew
    )

  return { tabs: capped, changed: hasChanged }
}

export type PruneForeignTabsResult = {
  /** True when a foreign tab was removed (caller should persist `tabs`). */
  changed: boolean
  tabs: Array<CanvasChatTab>
  /**
   * New active chat id when the ACTIVE tab was the foreign one and had to be
   * replaced; `null` means keep the current active (only non-active foreign
   * tabs were removed).
   */
  nextActiveId: string | null
}

/**
 * Pure brand-isolation pass for the tab strip. Given the open tabs, the active
 * brand id, and a `chatId → owning brandId` map (from `api.chats.queries
 * .chatBrandMap`), drop every PERSISTED tab whose chat belongs to a DIFFERENT
 * brand — the cross-brand bleed that full-page `/chat/<id>` adoption and
 * brand-switch races leave in a brand's localStorage bucket.
 *
 * Rules:
 *  - A tab is foreign iff it is NOT `isNew`, its id IS in `brandMap`, and the
 *    mapped brand ≠ `brandId`. Absent from the map = brand-new / not-yet-
 *    persisted = KEEP (a chat mid-creation must never vanish). `isNew`
 *    placeholders are always kept.
 *  - When the active tab itself is foreign, it is replaced: by the last
 *    surviving tab, or — if none survive — a fresh ephemeral `isNew` tab
 *    (`nextActiveId` set; the caller activates it WITHOUT mirroring the URL).
 *  - Returns `changed: false` (and the input unchanged) when nothing is
 *    foreign, so it is safe to call from a render effect.
 */
export function pruneForeignTabs({
  tabs,
  brandMap,
  brandId,
  activeChatId,
  generateChatId = nanoid,
}: {
  tabs: ReadonlyArray<CanvasChatTab>
  brandMap: Record<string, string>
  brandId: string
  activeChatId: string | null
  generateChatId?: () => string
}): PruneForeignTabsResult {
  const isForeign = (tab: CanvasChatTab): boolean => {
    if (tab.isNew) {
      return false
    }
    const owner = brandMap[tab.chatId]
    return owner !== undefined && owner !== brandId
  }

  if (!tabs.some(isForeign)) {
    return { changed: false, tabs: [...tabs], nextActiveId: null }
  }

  const kept = tabs.filter((tab) => !isForeign(tab))
  const wasActiveForeign =
    activeChatId != null &&
    tabs.some((tab) => tab.chatId === activeChatId && isForeign(tab))

  if (!wasActiveForeign) {
    return { changed: true, tabs: kept, nextActiveId: null }
  }
  if (kept.length === 0) {
    const freshId = generateChatId()
    return {
      changed: true,
      tabs: [{ chatId: freshId, isNew: true }],
      nextActiveId: freshId,
    }
  }
  return {
    changed: true,
    tabs: kept,
    nextActiveId: kept[kept.length - 1]!.chatId,
  }
}
